import { isUndefinedOrNull, convertTemperature, convertTemperatureUnit } from '#helper';

/**
 * @typedef {Object} LastMetric
 * @property {string} key - key based on identifier and sensor key.
 * @property {string} identifier - identifier.
 * @property {string|number} value - measured value of sensor.
 * @property {string} unit - measurement unit.
 */

/**
 * Generates array of last metrics.
 * @param {Object} metrics - metrics.
 * @param {Object} device - device.
 * @param {Object} account - account.
 * @param {Object} meta - meta.
 * @param {boolean} keepIdentifiersOrder - keepIdentifiersOrder.
 * @param {number} limitPerSensor - limit record per sensor.
 * @returns {LastMetric[]} array of last metrics.
 */

export const getLastMeasurements = (
  metrics = {},
  device,
  account,
  meta,
  keepIdentifiersOrder,
  limitPerSensor = 1
) => {
  const metaMetrics = meta?.find(m => m.name === device.type).metrics ?? [];

  return metaMetrics.reduce((a, c) => {
    if (c.sensors) {
      c.sensors.slice(0, limitPerSensor).forEach(s => {
        const measurements = metrics[c.identifier];
        const lastMetric = measurements?.[measurements.length - 1];

        if (!isUndefinedOrNull(lastMetric?.[s.key])) {
          const res = {
            identifier: c.identifier
          };
          if (c === 'temperature') {
            res.value = convertTemperature(lastMetric[s.key], account?.measurementSystem);
            res.unit = convertTemperatureUnit(account?.measurementSystem);
          } else {
            res.value = lastMetric[s.key];
            res.unit = s.unit;
          }
          a.push(res);
        } else if (keepIdentifiersOrder) {
          a.push({});
        }
      });
    }
    return a;
  }, []);
};

/**
 * Generates array of last existing metrics.
 * @param {Object} metrics - metrics.
 * @param {Object} device - device.
 * @param {Object} account - account.
 * @param {Object} meta - meta.
 * @returns {LastMetric[]} array of last existing metrics.
 */

export const getLastExistingMeasurements = (
  metrics = {},
  device,
  account,
  meta
) => {
  const metaMetrics = meta?.find(m => m.name === device.type).metrics ?? [];

  return metaMetrics.reduce((a, c) => {
    if (c.sensors) {
      const measurements = metrics[c.identifier];
      const measurementsLength = measurements?.length;

      if (measurementsLength > 0) {
        let sensor = c.sensors[0];

        if (c.identifier === 'temperature') {
          sensor = c.sensors.find(s => s.key === 'temp_probe') ||
            c.sensors.find(s => s.key === 'temp') || c.sensors.find(s => s.key === 't_in');
        }

        const res = {
          identifier: c.identifier
        };

        let index = measurementsLength - 1;
        while (index > 0) {
          const measurement = measurements[index];
          if (measurement[sensor?.key]) {
            if (c.identifier === 'temperature') {
              res.value = convertTemperature(measurement[sensor.key], account?.measurementSystem);
              res.unit = convertTemperatureUnit(account?.measurementSystem);
            } else {
              res.value = measurement[sensor.key];
              res.unit = sensor.unit;
            }
            break;
          } else {
            index -= 1;
          }
        }

        a.push(res);
      } else {
        a.push({});
      }
    }
    return a;
  }, []);
};

/**
 * Generates array of ranged metrics.
 * @param {Object} metrics - metrics.
 * @param {Object} device - device.
 * @param {Object} account - account.
 * @param {Object} meta - meta.
 * @returns {LastMetric[]} array of ranged metrics.
 */

export const getRangeMetrics = (
  metrics,
  device,
  account,
  meta
) => {
  const metaMetrics = meta?.find(m => m.name === device.type).metrics ?? [];

  return metaMetrics.reduce((a, c) => {
    const s = c.sensors[0];
    let min = metrics.min[s.key];
    let max = metrics.max[s.key];
    let minTimestamp = metrics.min[`${s.key}Timestamp`];
    let maxTimestamp = metrics.max[`${s.key}Timestamp`];
    if (c.identifier === 'temperature') {
      min = metrics.min.temp_probe ?? metrics.min[s.key];
      max = metrics.max.temp_probe ?? metrics.max[s.key];
      minTimestamp = metrics.min.temp_probeTimestamp ?? metrics.min[`${s.key}Timestamp`];
      maxTimestamp = metrics.max.temp_probeTimestamp ?? metrics.max[`${s.key}Timestamp`];
    }

    if (!isUndefinedOrNull(min) && !isUndefinedOrNull(max)) {
      const res = {
        identifier: c.identifier,
        dateRange:
          minTimestamp !== maxTimestamp ?
            { first: device.firstTimestamp, last: device.lastTimestamp } :
            minTimestamp
      };
      if (c.identifier === 'temperature') {
        min = convertTemperature(min, account?.measurementSystem);
        max = convertTemperature(max, account?.measurementSystem);
        res.label = min !== max ? `${min} - ${max}` : min;
        res.value = min !== max ? (min + max) / 2 : min;
        res.unit = convertTemperatureUnit(account?.measurementSystem);
        if (device.tempExcursion) res.excursion = true;
      } else {
        res.label = min !== max ? `${min} - ${max}` : min;
        res.value = min !== max ? (min + max) / 2 : min;
        res.unit = s.unit;
        if (c.identifier === 'humidity' && device.humiExcursion) res.excursion = true;
        if (c.identifier === 'battery' && device.batExcursion) res.excursion = true;
      }
      a.push(res);
    }
    return a;
  }, []);
};

/**
 * Generates array of device last metrics.
 * @param {Object} youngest - metrics
 * @param {Object} account - account.
 * @param {Object} meta - meta.
 * @param {String[]} identifiers - identifiers.
 * @returns {LastMetric[]} array of last metrics.
 */

export const getYoungestMetrics = (
  youngest = {},
  account,
  identifiers = [],
  keepIdentifiersOrder = false
) =>
  identifiers.reduce((a, c) => {
    const _ = youngest[c]?._;
    if (!isUndefinedOrNull(_?.value)) {
      const res = {
        identifier: c,
        description: _.description,
        timestamp: _.timestamp * 1000
      };
      if (c === 'temperature') {
        res.value = convertTemperature(_.value, account?.measurementSystem);
        res.unit = convertTemperatureUnit(account?.measurementSystem);
      } else {
        res.value = _.value;
        res.unit = _.unit;
      }
      a.push(res);
    } else if (keepIdentifiersOrder) {
      a.push({});
    }

    return a;
  }, []);

export const isActiveDevice = device => Object.prototype.hasOwnProperty.call(device, 'shipments') ||
  Object.prototype.hasOwnProperty.call(device, 'appliances');
